import React, { useEffect, useRef, useState } from 'react';
import { CodeBench, CodeBenchv2 } from '@codametrix/shared-views';
import { ServiceDesk } from './service-desk';
import { useSelector, useDispatch } from 'react-redux';
import { uiActions, Utils, codeBenchActions, commonEnums } from '@codametrix/ui-common';
import { setBackToWorklistPath } from '../../actions/ui';
import { getGlobalNavInstrumentation } from '../../core/nav-intrumentation.context';

type DualComponentProps = {
  detailProps: Accelerate.CodeBenchPanelData;
  detailMethods: Accelerate.CodeBenchPanelMethods;
  listProps: AppProps.ServiceDeskProps;
  listMethods: AppProps.ServiceDeskMethods;
  match: any;
  // CDMX-15947 — set by the /cmx/worklist/V2 route: render the dashboard-ui
  // coder ticker and suppress the SDK ProductivityTracker.
  coderViewV2?: boolean;
};

const ServiceDeskAndCodeBench = (props: DualComponentProps) => {
  const { params = {} } = props.match ?? {};
  const hasId = params.id !== undefined;

  const { UIView } = useSelector((state: CMx.ApplicationState) => {
    return state.ampUI;
  });
  const dispatch = useDispatch();
  const childRef = useRef<any>(null);
  const detailProps = {
    UIView,
    ...props.detailProps,
    ...props.detailMethods
  };

  const listProps = {
    ...props.listProps,
    ...props.listMethods
  };

  const [isDetailOpen, setIsDetailOpen] = useState<boolean>(hasId);

  useEffect(() => {
    if (!hasId && childRef?.current?.refreshContent) {
      childRef.current.refreshContent();
    }
    setIsDetailOpen(hasId);

    const pathname = window.location.pathname;
    const isWorklistListView =
      /^\/cmx\/worklist(\/|$)/.test(pathname) &&
      !/^\/cmx\/worklist\/[^/]+\/case\/[^/]+\/case-run\//.test(pathname);

    if (!hasId && isWorklistListView) {
      getGlobalNavInstrumentation()?.setGlobalContext('service-desk');
    }
  }, [params.id, hasId]);

  useEffect(() => {
    const segments = window.location.pathname.split('/').filter(Boolean);
    const basePath = `${segments[1] ?? 'worklist'}/`;

    dispatch(setBackToWorklistPath({ path: basePath }));
    dispatch(uiActions.setAmplifyView());
    dispatch(uiActions.setStandaloneMode());
    dispatch(codeBenchActions.setBenchMode(commonEnums.BenchMode.CODE));
    return () => {
      dispatch(uiActions.setAutomateView());
    };
  }, [dispatch]);

  return (
    <>
      <div
        style={
          isDetailOpen
            ? { display: 'none' }
            : { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%', width: '100%' }
        }
      >
        <ServiceDesk
          {...listProps}
          childRef={childRef}
          coderViewV2={props.coderViewV2}
        />
      </div>
      {isDetailOpen && params.id ? (
        Utils.doesPathEndWithV2() ? (
          // The codebench route always renders the bench in CODE mode; pass it
          // explicitly so a stale Redux benchMode can't select the grade branch.
          <CodeBenchv2
            {...detailProps}
            match={props.match}
            benchMode={commonEnums.BenchMode.CODE}
          />
        ) : (
          <CodeBench
            {...detailProps}
            match={props.match}
            benchMode={commonEnums.BenchMode.CODE}
          />
        )
      ) : null}
    </>
  );
};

export { ServiceDeskAndCodeBench };
